/**
 * DataLayer Utility Module
 *
 * PURPOSE:
 * This module provides a clean interface for pushing events to the dataLayer.
 * It handles:
 * 1. Consent checking (respects Cookiebot)
 * 2. Event queueing (for pre-consent events)
 * 3. Ecommerce data clearing (GA4 requirement)
 *
 * USAGE:
 * import { pushEvent, pushEcommerceEvent } from '~/js/utils/datalayer'
 * pushEvent('login', { method: 'email' })
 */

/**
 * Cookiebot consent object shape
 * This is what Cookiebot provides on window.Cookiebot.consent
 */
interface CookiebotConsent {
    necessary: boolean;    // Always true (required cookies)
    preferences: boolean;  // User preference cookies
    statistics: boolean;   // Analytics (GA4 needs this)
    marketing: boolean;    // Advertising (Google Ads needs this)
}

interface Cookiebot {
    consent: CookiebotConsent;
    consented: boolean;
}

/**
 * Extend Window to include our globals
 */
declare global {
    interface Window {
        dataLayer: Record<string, any>[];
        Cookiebot?: Cookiebot;
        __dlQueue?: Record<string, any>[];
    }
}

/**
 * Check if user has given consent for a specific category
 *
 * @param type - The consent category to check
 * @returns boolean - true if consented, false otherwise
 *
 */
export function hasConsent(type: keyof CookiebotConsent = 'statistics'): boolean {
    // If Cookiebot hasn't loaded, assume no consent
    if (typeof window.Cookiebot === 'undefined') {
        return false;
    }
    return window.Cookiebot.consent[type] === true;
}

/**
 * Check if Cookiebot has loaded and user has made a choice
 */
export function hasConsentDecision(): boolean {
    return typeof window.Cookiebot !== 'undefined' && window.Cookiebot.consented;
}

/**
 * Push a simple event to the dataLayer
 *
 * @param eventName - The event name (e.g., 'login', 'sign_up')
 * @param eventData - Additional data to include
 * @param requiresConsent - Whether to check consent first (default: true)
 *
 * EXAMPLE:
 * pushEvent('login', { method: 'email' })
 * pushEvent('page_view', { page_type: 'blog' }, false) // No consent needed
 */
export function pushEvent(
    eventName: string,
    eventData: Record<string, any> = {},
    requiresConsent: boolean = true
): void {
    const payload = {
        event: eventName,
        ...eventData
    };

    if (!requiresConsent || hasConsent('statistics')) {
        window.dataLayer.push(payload);
    } else if (!hasConsentDecision()) {
        // User hasn't decided yet - queue the event
        window.__dlQueue = window.__dlQueue || [];
        window.__dlQueue.push(payload);
    }
    // If user declined, we simply don't push (and don't queue)
}

/**
 * Push an ecommerce event to the dataLayer
 *
 * IMPORTANT: GA4 requires clearing ecommerce object before pushing new data
 * This prevents data from previous events bleeding into new ones
 *
 * @param eventName - The ecommerce event name
 * @param ecommerceData - The ecommerce object
 * @param additionalData - Any extra data (like user_data)
 *
 * EXAMPLE:
 * pushEcommerceEvent('view_item', {
 *   items: [{ item_id: '123', item_name: 'Developer Job' }]
 * })
 */
export function pushEcommerceEvent(
    eventName: string,
    ecommerceData: Record<string, any>,
    additionalData: Record<string, any> = {}
): void {
    const cookiebotLoaded = typeof window.Cookiebot !== 'undefined';

    // If Cookiebot isn't loaded (e.g., UAT environment), push directly
    // This allows testing without consent management
    if (!cookiebotLoaded) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
            event: eventName,
            ecommerce: ecommerceData,
            ...additionalData
        });
        return;
    }

    if (!hasConsent('statistics')) {
        // Queue if no decision yet
        if (!hasConsentDecision()) {
            window.__dlQueue = window.__dlQueue || [];
            window.__dlQueue.push({
                event: eventName,
                ecommerce: ecommerceData,
                ...additionalData
            });
        }
        return;
    }

    // CRITICAL: Clear ecommerce object first
    // This is a GA4 requirement to prevent data leakage between events
    window.dataLayer.push({ ecommerce: null });

    // Now push the actual event
    window.dataLayer.push({
        event: eventName,
        ecommerce: ecommerceData,
        ...additionalData
    });
}
